/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ChannelReference } from '../models/ChannelReference';
import type { ConnectionKey } from '../models/ConnectionKey';
import type { DeviceCapabilities } from '../models/DeviceCapabilities';
import type { DeviceInfo } from '../models/DeviceInfo';
import type { DeviceMode } from '../models/DeviceMode';
import type { DeviceModeValue } from '../models/DeviceModeValue';
import type { DeviceResponse } from '../models/DeviceResponse';
import type { DeviceSessionIds } from '../models/DeviceSessionIds';
import type { DeviceStatistics } from '../models/DeviceStatistics';
import type { SliderSettings } from '../models/SliderSettings';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class InfoService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get the device slider settings.
     * Get the device slider settings.
     *
     * To change device slider settings, use the Handy onboarding app.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device slider settings.
     * @throws ApiError
     */
    public getSliderSettings(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: SliderSettings;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/settings/slider',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Get the device information.
     * Get the device information.
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device information.
     * @throws ApiError
     */
    public getDeviceInfo(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: DeviceInfo;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/info',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Get the current device mode.
     * Get the current device mode.
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device mode.
     * @throws ApiError
     */
    public getMode(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: DeviceMode;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/mode',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * @deprecated
     * Set the device mode.
     * `DEPRECATEAD`. Use `/mode2` endpoint instead which returns additional information in the response. Kept for backwards compatibility. Set the device mode.
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody Device mode
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device mode.
     * @throws ApiError
     */
    public setMode(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: {
            mode: DeviceModeValue;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: string;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/mode',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Set the device mode.
     * Set the device mode and returns the active mode and mode_session_id.
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody Device mode
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device mode.
     * @throws ApiError
     */
    public setMode2(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: {
            mode: DeviceModeValue;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: DeviceMode;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/mode2',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Check device connectivity.
     * Check if the device with the specific connection key is connected.
     *
     * This is the fastest way to check device connectivity.
     *
     * To receive continouos updates on device connectivity and device state use the SSE endpoint to subscribe to device events.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Machine connected status
     * @throws ApiError
     */
    public isConnected(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        /**
         * Connected response
         */
        result?: {
            /**
             * Connected status
             */
            connected: boolean;
        };
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/connected',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            errors: {
                400: `Bad request.`,
            },
        });
    }

    /**
     * Device message statistics.
     * The device message statistics provides information about the device message traffic.
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device statistics.
     * @throws ApiError
     */
    public getStatistics(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: DeviceStatistics;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/statistics',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Get the device capabilities.
     * Get the device capabilities.
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device capabilities.
     * @throws ApiError
     */
    public getDeviceCapabilities(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: DeviceCapabilities;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/capabilities',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Get the device session IDs.
     * Get the device's session IDs.
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device sessions IDs.
     * @throws ApiError
     */
    public getDeviceSessionIds(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: DeviceSessionIds;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/sids',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

}
